import { getMeterReading, getMeterReadingFromWheels, getMeterWheelDigit, getRollingMeterReading } from './meterReading';

const otsuThreshold = histogram => {
  const total = histogram.reduce((sum, count) => sum + count, 0);
  const weighted = histogram.reduce((sum, count, value) => sum + count * value, 0);
  let background = 0;
  let backgroundWeighted = 0;
  let bestVariance = -1;
  let threshold = 0;
  for (let value = 0; value < 256; value += 1) {
    background += histogram[value];
    if (!background) continue;
    const foreground = total - background;
    if (!foreground) break;
    backgroundWeighted += value * histogram[value];
    const difference = backgroundWeighted / background - (weighted - backgroundWeighted) / foreground;
    const variance = background * foreground * difference ** 2;
    if (variance > bestVariance) {
      bestVariance = variance;
      threshold = value;
    }
  }
  return threshold;
};

const findComponents = (mask, width, height) => {
  const components = [];
  const visited = new Uint8Array(mask.length);
  for (let index = 0; index < mask.length; index += 1) {
    if (!mask[index] || visited[index]) continue;
    const pixels = [index];
    visited[index] = 1;
    let x0 = width;
    let x1 = 0;
    let y0 = height;
    let y1 = 0;
    for (let cursor = 0; cursor < pixels.length; cursor += 1) {
      const pixel = pixels[cursor];
      const x = pixel % width;
      const y = Math.floor(pixel / width);
      x0 = Math.min(x0, x); x1 = Math.max(x1, x);
      y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      for (const neighbor of [x > 0 ? pixel - 1 : -1, x + 1 < width ? pixel + 1 : -1, pixel - width, pixel + width]) {
        if (neighbor < 0 || neighbor >= mask.length || visited[neighbor] || !mask[neighbor]) continue;
        visited[neighbor] = 1;
        pixels.push(neighbor);
      }
    }
    components.push({ pixels, x0, x1, y0, y1 });
  }
  return components;
};

export const findMeterCounterRegions = ({ data, width, height }) => {
  const gray = new Uint8Array(width * height);
  const histogram = new Array(256).fill(0);
  for (let index = 0; index < gray.length; index += 1) {
    const value = Math.round(data[index * 4] * 0.299 + data[index * 4 + 1] * 0.587 + data[index * 4 + 2] * 0.114);
    gray[index] = value;
    histogram[value] += 1;
  }
  const low = histogram.findIndex(count => count);
  const high = 255 - [...histogram].reverse().findIndex(count => count);
  if (high - low < 30) return [];
  const regions = [];
  for (const fraction of [0.65, 0.8]) {
    const mask = gray.map(value => Number(value > low + (high - low) * fraction));
    const components = findComponents(mask, width, height).filter(component => {
      const h = component.y1 - component.y0 + 1;
      const w = component.x1 - component.x0 + 1;
      return h >= height * 0.2 && h <= height * 0.7 && w >= h * 0.2 && w <= h * 1.4 &&
        component.pixels.length >= w * h * 0.25;
    }).sort((a, b) => a.x0 - b.x0);
    for (const first of components) {
      const h = first.y1 - first.y0 + 1;
      const row = components.filter(component =>
        component.x0 >= first.x0 && Math.abs(component.y0 - first.y0) < h * 0.3 &&
        Math.abs((component.y1 - component.y0 + 1) - h) < h * 0.4);
      const run = [first];
      for (const component of row.slice(1)) {
        const previous = run[run.length - 1];
        const distance = (component.x0 + component.x1 - previous.x0 - previous.x1) / 2;
        if (distance < h * 0.65) continue;
        if (distance > h * 1.7) break;
        run.push(component);
      }
      if (run.length < 4) continue;
      const distances = run.slice(1).map((component, index) =>
        (component.x0 + component.x1 - run[index].x0 - run[index].x1) / 2).sort((a, b) => a - b);
      const pitch = distances[Math.floor(distances.length / 2)];
      if (distances.some(distance => Math.abs(distance - pitch) > pitch * 0.2)) continue;
      const right = (run[run.length - 1].x0 + run[run.length - 1].x1) / 2 + pitch / 2;
      const y0 = Math.max(0, Math.round(Math.min(...run.map(component => component.y0)) - h * 0.3));
      const y1 = Math.min(height, Math.round(Math.max(...run.map(component => component.y1)) + h * 0.15));
      for (const count of [6, 5]) {
        const left = Math.round(right - count * pitch);
        if (left < 0 || right > width || count < run.length || right - left > width * 0.95) continue;
        const region = { left, top: y0, width: Math.round(count * pitch), height: y1 - y0 };
        if (regions.some(existing => Math.abs(existing.left - left) < pitch * 0.3 &&
          Math.abs(existing.width - region.width) < pitch * 0.3 && Math.abs(existing.top - y0) < h * 0.3)) continue;
        regions.push(region);
      }
    }
  }
  return regions.slice(0, 4);
};

export const findMeterWindowRows = ({ data, width, height }) => {
  const gray = new Uint8Array(width * height);
  const histogram = new Array(256).fill(0);
  for (let index = 0; index < gray.length; index += 1) {
    const value = Math.round(data[index * 4] * 0.299 + data[index * 4 + 1] * 0.587 + data[index * 4 + 2] * 0.114);
    gray[index] = value;
    histogram[value] += 1;
  }
  const low = histogram.findIndex(count => count);
  const high = 255 - [...histogram].reverse().findIndex(count => count);
  if (high - low < 30) return [];
  const threshold = otsuThreshold(histogram);
  const rows = [];
  for (const level of [threshold * 1.2, threshold]) {
    const components = findComponents(gray.map(value => Number(value > level)), width, height);
    const windows = components.filter(component => {
      const h = component.y1 - component.y0 + 1;
      const w = component.x1 - component.x0 + 1;
      return h >= Math.max(12, height * 0.12) && h < height * 0.7 &&
        w >= h * 0.3 && w < h * 1.2 && component.pixels.length > w * h * 0.4;
    }).sort((a, b) => a.x0 - b.x0);
    for (const first of windows) {
      const h = first.y1 - first.y0 + 1;
      const row = windows.filter(component => component.x0 >= first.x0 &&
        Math.abs(component.y0 - first.y0) < h * 0.25 &&
        Math.abs(component.y1 - first.y1) < h * 0.25);
      if (row.length < 5 || row.length > 6) continue;
      if (row.reduce((sum, component) => sum + component.pixels.length /
        ((component.x1 - component.x0 + 1) * (component.y1 - component.y0 + 1)), 0) / row.length < 0.6) continue;
      const distances = row.slice(1).map((component, index) =>
        (component.x0 + component.x1 - row[index].x0 - row[index].x1) / 2).sort((a, b) => a - b);
      const pitch = distances[Math.floor(distances.length / 2)];
      if (pitch < h * 0.65 || pitch > h * 1.5 ||
        distances.some(distance => Math.abs(distance - pitch) > pitch * 0.25)) continue;
      if (windows.some(component => component.x1 < first.x0 && Math.abs(component.y0 - first.y0) < h * 0.25 &&
        Math.abs(component.y1 - first.y1) < h * 0.25 &&
        first.x0 - component.x0 < pitch * 1.3)) continue;
      const bounds = row.map(component => {
        const fragments = components.filter(fragment => fragment.x0 >= component.x0 - pitch * 0.2 &&
          fragment.x1 <= component.x1 && fragment.y0 >= component.y0 - h * 0.1 &&
          fragment.y1 <= component.y1 + h * 0.1);
        const left = Math.min(component.x0, ...fragments.map(fragment => fragment.x0));
        return { left, top: component.y0, width: component.x1 - left + 1, height: component.y1 - component.y0 + 1 };
      });
      if (!rows.some(existing => existing.length === bounds.length &&
        Math.abs(existing[0].left - bounds[0].left) < pitch * 0.3)) rows.push(bounds);
    }
  }
  return rows.slice(0, 2);
};

const cropMeterImage = ({ data, width }, region) => {
  const pixels = new Uint8ClampedArray(region.width * region.height * 4);
  for (let y = 0; y < region.height; y += 1) {
    const start = ((region.top + y) * width + region.left) * 4;
    pixels.set(data.subarray(start, start + region.width * 4), y * region.width * 4);
  }
  return { data: pixels, width: region.width, height: region.height };
};

export const prepareMeterWindowImages = (image, windows) => windows.map(window =>
  [0.85, 0.75].map(right => {
    const cell = cropMeterImage(image, {
      left: window.left + Math.round(window.width * 0.05), top: window.top,
      width: Math.round(window.width * (right - 0.05)), height: window.height
    });
    const gray = new Uint8Array(cell.width * cell.height);
    let low = 255;
    let high = 0;
    for (let index = 0; index < gray.length; index += 1) {
      const value = Math.round(cell.data[index * 4] * 0.299 + cell.data[index * 4 + 1] * 0.587 + cell.data[index * 4 + 2] * 0.114);
      gray[index] = value;
      low = Math.min(low, value); high = Math.max(high, value);
    }
    const mask = gray.map(value => Number(high - low >= 30 && value < (low + high) / 2));
    for (let y = 0; y < cell.height; y += 1) {
      if (y > cell.height * 0.2 && y < cell.height * 0.8) continue;
      const start = y * cell.width;
      if (mask.subarray(start, start + cell.width).reduce((sum, value) => sum + value, 0) > cell.width * 0.85) {
        mask.fill(0, start, start + cell.width);
      }
    }
    const components = findComponents(mask, cell.width, cell.height).filter(component =>
      component.y1 - component.y0 > cell.height * 0.4 &&
      component.x1 - component.x0 > cell.width * 0.1 && component.x0 < cell.width * 0.5)
      .sort((a, b) => b.pixels.length - a.pixels.length);
    const selected = new Set(components[0]?.pixels);
    return [false, true].map(binary => {
      const width = 160;
      const height = 180;
      const pixels = new Uint8ClampedArray(width * height * 4).fill(255);
      const bounds = binary && components[0] ? components[0] : {
        x0: 0, x1: cell.width - 1, y0: Math.round(cell.height * 0.04), y1: Math.round(cell.height * 0.96) - 1
      };
      const glyphWidth = bounds.x1 - bounds.x0 + 1;
      const glyphHeight = bounds.y1 - bounds.y0 + 1;
      const scale = Math.min(95 / glyphWidth, 112 / glyphHeight);
      const left = Math.floor((width - glyphWidth * scale) / 2);
      const top = Math.floor((height - glyphHeight * scale) / 2);
      for (let y = 0; y < glyphHeight * scale; y += 1) {
        for (let x = 0; x < glyphWidth * scale; x += 1) {
          const source = (bounds.y0 + Math.floor(y / scale)) * cell.width + bounds.x0 + Math.floor(x / scale);
          const value = binary ? (selected.has(source) ? 0 : 255) : gray[source];
          const target = ((top + y) * width + left + x) * 4;
          pixels[target] = value; pixels[target + 1] = value; pixels[target + 2] = value;
        }
      }
      return { data: pixels, width, height };
    });
  }));

const recognizeMeterWindows = async (worker, pixels, windows, encodeImage, isActive, getDigitWorker) => {
  const wheels = prepareMeterWindowImages(pixels, windows);
  const candidates = [];
  for (const [index, variants] of wheels.entries()) {
    let candidate = null;
    for (const [gray] of variants) {
      const { data } = await worker.recognize(encodeImage(gray), { tessedit_pageseg_mode: '10' });
      if (!isActive()) return null;
      const text = String(data.text || '').replace(/\s/g, '');
      const digit = index === wheels.length - 1 || /^\d$/.test(text) ? getMeterWheelDigit(text, data.confidence) : null;
      if (digit !== null && candidate && digit !== candidate.digit) return null;
      if (digit !== null && (!candidate || data.confidence > candidate.confidence)) {
        candidate = { digit, confidence: data.confidence, rollover: text === '90' || text === '09' };
      }
    }
    candidates.push(candidate);
  }
  if (candidates.some(candidate => !candidate || candidate.confidence < 90)) {
    const digitWorker = await getDigitWorker();
    if (!isActive()) return null;
    for (const [index, variants] of wheels.entries()) {
      if (candidates[index]?.confidence >= 90) continue;
      const readings = [];
      for (const [, binary] of variants) {
        const { data } = await digitWorker.recognize(encodeImage(binary), { tessedit_pageseg_mode: '10' });
        if (!isActive()) return null;
        const text = String(data.text || '').replace(/\s/g, '');
        if (/^\d$/.test(text) && Number.isFinite(data.confidence) && data.confidence >= 60) {
          readings.push({ digit: text, confidence: data.confidence });
        }
      }
      // Classical glyph scores are lower; require agreement across both crops below 75.
      const consistent = readings.length === variants.length && readings.every(reading => reading.digit === readings[0].digit);
      const best = readings.sort((a, b) => b.confidence - a.confidence)[0];
      if (best && (best.confidence >= 75 || consistent)) {
        if (readings.some(reading => reading.digit !== best.digit && reading.confidence >= 75)) return null;
        if (candidates[index] && candidates[index].digit !== best.digit) return null;
        candidates[index] = { ...best, rollover: candidates[index]?.rollover };
      }
    }
  }
  if (candidates.some(candidate => !candidate)) return null;
  const precision = windows.length - 4;
  const digits = candidates.map(candidate => candidate.digit).join('');
  if (candidates[candidates.length - 1].rollover) {
    const beforeRollover = (Number(`${digits.slice(0, -1)}9`) / 10 ** precision).toFixed(precision);
    return getRollingMeterReading(beforeRollover, '0', 75);
  }
  return (Number(digits) / 10 ** precision).toFixed(precision);
};

export const prepareMeterWheelImages = ({ data, width, height }, decimalPlaces, localized = false, contrast = 0.65) => {
  if (![1, 2].includes(decimalPlaces)) throw new Error('Meter precision must be one or two decimal places.');
  if (width < 60 || height < 20 || data.length !== width * height * 4) throw new Error('The meter image is too small or incomplete.');
  let frameLow = 255;
  let frameHigh = 0;
  if (localized) {
    for (let index = 0; index < data.length; index += 4) {
      const value = Math.round(data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114);
      frameLow = Math.min(frameLow, value);
      frameHigh = Math.max(frameHigh, value);
    }
  }
  const digitCount = 4 + decimalPlaces;
  const wheels = [];
  for (let digit = 0; digit < digitCount; digit += 1) {
    const left = Math.round(width * (digit + 0.08) / digitCount);
    const right = Math.round(width * (digit + 0.92) / digitCount);
    const top = Math.round(height * (localized ? 0.1 : 0.2));
    const bottom = Math.round(height * 0.95);
    const cellWidth = right - left;
    const cellHeight = bottom - top;
    const gray = new Uint8Array(cellWidth * cellHeight);
    const histogram = new Array(256).fill(0);
    for (let y = 0; y < cellHeight; y += 1) {
      for (let x = 0; x < cellWidth; x += 1) {
        const index = ((y + top) * width + x + left) * 4;
        const value = Math.round(data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114);
        gray[y * cellWidth + x] = value;
        histogram[value] += 1;
      }
    }
    const low = histogram.findIndex(count => count > 0);
    const high = 255 - [...histogram].reverse().findIndex(count => count > 0);
    const threshold = localized ? frameLow + (frameHigh - frameLow) * contrast : otsuThreshold(histogram);
    const variants = [];
    for (const darkBackground of [true, false]) {
      const mask = gray.map(value => Number(high - low >= 30 && (value > threshold) === darkBackground));
      for (let y = 0; y < cellHeight; y += 1) {
        if (y > cellHeight * 0.15 && y < cellHeight * 0.85) continue;
        let count = 0;
        for (let x = 0; x < cellWidth; x += 1) count += mask[y * cellWidth + x];
        if (count > cellWidth * 0.8) mask.fill(0, y * cellWidth, (y + 1) * cellWidth);
      }
      for (let x = 0; x < cellWidth; x += 1) {
        if (x > cellWidth * 0.15 && x < cellWidth * 0.85) continue;
        let count = 0;
        for (let y = 0; y < cellHeight; y += 1) count += mask[y * cellWidth + x];
        if (count > cellHeight * 0.8) {
          for (let y = 0; y < cellHeight; y += 1) mask[y * cellWidth + x] = 0;
        }
      }
      const components = findComponents(mask, cellWidth, cellHeight)
        .filter(component => component.y1 - component.y0 >= cellHeight * 0.25 &&
          component.x1 - component.x0 >= cellWidth * 0.08)
        .map(component => ({ ...component, score: component.pixels.length *
          (1 - Math.abs((component.x0 + component.x1) / 2 / cellWidth - 0.5)) }));
      components.sort((a, b) => b.score - a.score);
      const component = components[0];
      const size = 160;
      const pixels = new Uint8ClampedArray(size * size * 4).fill(255);
      if (component) {
        const glyphWidth = component.x1 - component.x0 + 1;
        const glyphHeight = component.y1 - component.y0 + 1;
        const scale = Math.min(100 / glyphWidth, 112 / glyphHeight);
        const offsetX = Math.floor((size - glyphWidth * scale) / 2);
        const offsetY = Math.floor((size - glyphHeight * scale) / 2);
        const selected = new Set(component.pixels);
        for (let y = 0; y < glyphHeight * scale; y += 1) {
          for (let x = 0; x < glyphWidth * scale; x += 1) {
            if (!selected.has((component.y0 + Math.floor(y / scale)) * cellWidth + component.x0 + Math.floor(x / scale))) continue;
            const index = ((offsetY + y) * size + offsetX + x) * 4;
            pixels[index] = 0; pixels[index + 1] = 0; pixels[index + 2] = 0;
          }
        }
      }
      variants.push({ data: pixels, width: size, height: size });
    }
    wheels.push(variants);
  }
  return wheels;
};

export const recognizeMeterWheels = async (worker, wheels, decimalPlaces, isActive = () => true, alternatives = []) => {
  if (![1, 2].includes(decimalPlaces) || wheels.length !== 4 + decimalPlaces) throw new Error('The meter wheel count does not match its precision.');
  const readings = [];
  for (const [index, variants] of wheels.entries()) {
    const candidates = [];
    for (const [polarity, primary] of variants.entries()) {
      let candidate = null;
      for (const image of [primary, ...alternatives.map(layout => layout[index][polarity])]) {
        for (const mode of ['8', '10']) {
          if (!isActive()) return null;
          const { data } = await worker.recognize(image, { tessedit_pageseg_mode: mode });
          if (!isActive()) return null;
          const text = String(data.text || '').replace(/\s/g, '');
          // A needle can partially cover the first hour digit; require stable frames before filling.
          const minimumConfidence = index === 0 ? 50 : 75;
          const digit = /^\d$/.test(text) && Number.isFinite(data.confidence) && data.confidence >= minimumConfidence ?
            text : getMeterWheelDigit(text, data.confidence);
          if (digit !== null && (!candidate || data.confidence > candidate.confidence)) {
            candidate = { digit, confidence: data.confidence };
          }
          if (candidate?.confidence >= 90) break;
        }
        if (candidate?.confidence >= 90) break;
      }
      candidates.push(candidate);
    }
    readings.push(candidates);
  }
  let digits = '';
  // Hours share one wheel color; decimal wheels may use the opposite color.
  for (const group of [readings.slice(0, 4), readings.slice(4)]) {
    const scores = [0, 1].map(polarity => group.every(candidates => candidates[polarity]) ?
      group.reduce((score, candidates) => score + candidates[polarity].confidence, 0) : -1);
    if (Math.max(...scores) < 0) return null;
    if (scores[0] >= 0 && scores[1] >= 0 && Math.abs(scores[0] - scores[1]) < 10 &&
        group.some(candidates => candidates[0].digit !== candidates[1].digit)) return null;
    const polarity = scores[0] >= scores[1] ? 0 : 1;
    digits += group.map(candidates => candidates[polarity].digit).join('');
  }
  if (!new RegExp(`^\\d{${4 + decimalPlaces}}$`).test(digits)) return null;
  return (Number(digits) / 10 ** decimalPlaces).toFixed(decimalPlaces);
};

const recognizeCounterImage = async (worker, image, readPixels, encodeImage, isActive, localized = false) => {
  if (!isActive()) return null;
  const { data } = await worker.recognize(image);
  if (!isActive()) return null;
  const wholeReading = getMeterReading(data.text, data.confidence);
  const precisions = wholeReading ? [wholeReading.split('.')[1].length] : [2, 1];
  const readings = [];
  let pixels;
  for (const decimalPlaces of precisions) {
    const lastDigitLeft = Math.round(image.width * (3 + decimalPlaces) / (4 + decimalPlaces));
    const { data: lastWheel } = await worker.recognize(image, {
      rectangle: { left: lastDigitLeft, top: 0, width: image.width - lastDigitLeft, height: image.height },
      tessedit_pageseg_mode: '6'
    });
    if (!isActive()) return null;
    let value = wholeReading;
    if (!value) {
      const { data: prefix } = await worker.recognize(image, {
        rectangle: { left: 0, top: 0, width: lastDigitLeft, height: image.height },
        tessedit_pageseg_mode: '7'
      });
      if (!isActive()) return null;
      value = getMeterReadingFromWheels(prefix.text, prefix.confidence, lastWheel.text, lastWheel.confidence, decimalPlaces);
    }
    if (!value) {
      pixels = pixels || readPixels();
      const wheels = prepareMeterWheelImages(pixels, decimalPlaces, localized)
        .map(variants => variants.map(encodeImage));
      const alternatives = localized ? [prepareMeterWheelImages(pixels, decimalPlaces, true, 0.7)
        .map(variants => variants.map(encodeImage))] : [];
      value = await recognizeMeterWheels(worker, wheels, decimalPlaces, isActive, alternatives);
      if (!isActive()) return null;
    }
    if (value) readings.push(getRollingMeterReading(value, lastWheel.text, lastWheel.confidence));
  }
  // If both wheel counts give valid but different readings, wait for a clearer frame.
  return readings.length === 1 ? readings[0] : null;
};

export const recognizeMeterImage = async (worker, image, readPixels, encodeImage, isActive = () => true, getDigitWorker) => {
  if (!isActive()) return null;
  const pixels = readPixels();
  if (getDigitWorker) {
    for (const windows of findMeterWindowRows(pixels)) {
      const value = await recognizeMeterWindows(worker, pixels, windows, encodeImage, isActive, getDigitWorker);
      if (!isActive()) return null;
      if (value) return value;
    }
  }
  const regions = findMeterCounterRegions(pixels);
  for (const region of regions) {
    const counter = cropMeterImage(pixels, region);
    const encoded = encodeImage(counter);
    const value = await recognizeCounterImage(worker, encoded, () => counter, encodeImage, isActive, true);
    if (!isActive()) return null;
    if (value) return value;
  }
  return recognizeCounterImage(worker, image, () => pixels, encodeImage, isActive);
};
